import { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { MdCloudUpload, MdGridView, MdList, MdContentCopy } from 'react-icons/md';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import type { ProcessServerConfigFunction } from 'filepond';
import type { TUploadedFile } from '@onesource/schemas';
import { axiosServiceInstance, useGetMediaFilesQuery, getMediaFilesQueryKey } from '@onesource/services';
import { MediaGrid } from './media-grid';
import { MediaUploadDialog } from './media-upload-dialog';

const MEDIA_FOLDER = 'members-panel';
const VIEW_PREF_KEY = 'lc-media-view';

type TView = 'grid' | 'table';

function getSavedView(): TView {
  try {
    const saved = localStorage.getItem(VIEW_PREF_KEY);
    if (saved === 'grid' || saved === 'table') {
      return saved;
    }
  } catch {
    // ignore
  }
  return 'grid';
}

function saveView(view: TView) {
  try {
    localStorage.setItem(VIEW_PREF_KEY, view);
  } catch {
    // ignore
  }
}

const getPresignedUrl = async (
  key: string,
  contentType: string,
  folder?: string,
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>('/upload/presigned-url', {
    params: { key, contentType, ...(folder && { folder }) },
  });
  return res.data;
};

const buildProcessHandler =
  (folder: string): ProcessServerConfigFunction =>
  async (_fieldName, file, _metadata, load, error, progress, abort) => {
    const key = `${Date.now()}-${(file as File).name}`;
    const contentType = (file as File).type;

    try {
      const { uploadUrl } = await getPresignedUrl(key, contentType, folder);

      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          progress(true, e.loaded, e.total);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          load(key);
        } else {
          error('Upload failed');
        }
      };

      xhr.onerror = () => error('Upload failed');

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file as File);

      return {
        abort: () => {
          xhr.abort();
          abort();
        },
      };
    } catch {
      error('Failed to get presigned URL');
    }
  };

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function MediaTableView({ files, isLoading }: { files: TUploadedFile[]; isLoading: boolean }) {
  const handleCopy = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    enqueueSnackbar('URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Preview</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Uploaded</TableCell>
            <TableCell align="right">URL</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  No files uploaded yet. Click "Upload" to add files.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            files.map(file => (
              <TableRow key={file.key} hover>
                <TableCell sx={{ width: 56, py: 0.75 }}>
                  {file.publicUrl ? (
                    <Box
                      component="img"
                      src={file.publicUrl}
                      alt={file.filename}
                      sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {file.filename}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatBytes(file.size)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(file.lastModified).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {file.publicUrl && (
                    <Tooltip title="Copy URL" arrow>
                      <IconButton size="small" onClick={() => handleCopy(file.publicUrl!)}>
                        <MdContentCopy size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function MediaPage() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<TView>(getSavedView);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data: pageData, isLoading } = useGetMediaFilesQuery();
  const files = pageData?.data ?? [];

  const processHandler = buildProcessHandler(MEDIA_FOLDER);

  const handleUploaded = useCallback(
    (_key: string) => {
      queryClient.invalidateQueries({ queryKey: getMediaFilesQueryKey() });
    },
    [queryClient],
  );

  const handleViewChange = useCallback((next: TView) => {
    setView(next);
    saveView(next);
  }, []);

  return (
    <>
      <Helmet>
        <title>Media Library</title>
        <meta name="description" content="Manage your uploaded media files" />
      </Helmet>

      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Media Library
          </Typography>

          {/* View toggle */}
          <Tooltip title="Grid view" arrow>
            <IconButton
              size="small"
              color={view === 'grid' ? 'primary' : 'default'}
              onClick={() => handleViewChange('grid')}
            >
              <MdGridView size={20} />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view" arrow>
            <IconButton
              size="small"
              color={view === 'table' ? 'primary' : 'default'}
              onClick={() => handleViewChange('table')}
            >
              <MdList size={20} />
            </IconButton>
          </Tooltip>

          <Button variant="contained" startIcon={<MdCloudUpload />} onClick={() => setUploadOpen(true)}>
            Upload
          </Button>
        </Box>

        {/* Content */}
        {view === 'grid' ? (
          <MediaGrid files={files} isLoading={isLoading} />
        ) : (
          <MediaTableView files={files} isLoading={isLoading} />
        )}
      </Box>

      <MediaUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        processHandler={processHandler}
        onUploaded={handleUploaded}
      />
    </>
  );
}
